import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Heart, GraduationCap, Loader2, Shield, Repeat, LogIn, CalendarIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

import API from "@/apiConfig";
const GIVING_IMG = "https://customer-assets.emergentagent.com/job_033adeb0-4821-466d-a137-c74b40513737/artifacts/m7a95lsk_untitled-8879.jpg";
const PRESET_AMOUNTS = [25, 50, 100, 250, 500];

export default function GivingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [fund, setFund] = useState("general");
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [remember, setRemember] = useState(false);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isSignedIn, setIsSignedIn] = useState(false);

  // Recurring state
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState("monthly");
  const [coverFees, setCoverFees] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [loginBannerDismissed, setLoginBannerDismissed] = useState(false);
  const [givingPages, setGivingPages] = useState([]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("wagc_giving_pref");
      if (saved) {
        const pref = JSON.parse(saved);
        if (pref.fund) setFund(pref.fund);
        if (pref.amount) setAmount(String(pref.amount));
        setRemember(true);
      }
    } catch {}
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => { setUserEmail(r.data.email || ""); setIsSignedIn(true); })
      .catch(() => { setIsSignedIn(false); });
    axios.get(`${API}/pages-giving`)
      .then((r) => setGivingPages(r.data.options || []))
      .catch(() => {});
  }, []);

  // Handle PayPal return query params
  useEffect(() => {
    const recurringStatus = searchParams.get("recurring");
    if (!recurringStatus) return;
    const subId = searchParams.get("subscription_id");
    setSearchParams({}, { replace: true });
    if (recurringStatus === "success") {
      toast.success("Recurring gift set up successfully! You'll be charged automatically on your chosen schedule.");
      // Confirm with backend to sync PayPal status → active
      if (subId) {
        axios.post(`${API}/giving/recurring/confirm`, { subscription_id: subId }, { withCredentials: true })
          .then((r) => { if (r.data.updated) toast.success("Subscription confirmed as active!"); })
          .catch(() => { /* silent — webhook will catch it */ });
      }
    } else if (recurringStatus === "cancel") {
      toast.info("Recurring gift setup was cancelled. You can try again anytime.");
    }
  }, [searchParams, setSearchParams]);

  const selectedAmount = amount === "custom" ? parseFloat(customAmount) : parseFloat(amount);

  const handleDonate = async () => {
    if (!selectedAmount || selectedAmount < 1) {
      toast.error("Please select or enter a valid amount");
      return;
    }
    setLoading(true);
    if (remember) {
      localStorage.setItem("wagc_giving_pref", JSON.stringify({ fund, amount: selectedAmount }));
    } else {
      localStorage.removeItem("wagc_giving_pref");
    }

    try {
      if (isRecurring) {
        // Recurring path — requires auth
        const payload = {
          amount: selectedAmount,
          frequency,
          fund,
          cover_fees: coverFees,
          note: note.trim() || undefined,
        };
        if (startDate) {
          // Convert to ISO 8601 UTC midnight
          const d = new Date(startDate);
          d.setUTCHours(0, 0, 0, 0);
          payload.start_time = d.toISOString().replace(".000Z", "Z");
        }
        const res = await axios.post(`${API}/giving/recurring`, payload, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });
        if (res.data.approval_url) {
          toast.success("Redirecting to PayPal to approve your recurring gift...");
          window.location.href = res.data.approval_url;
        } else {
          toast.error("Unable to start recurring gift setup. Please try again.");
        }
      } else {
        // One-time path (existing)
        const res = await axios.post(`${API}/giving/paypal/create`, {
          amount: selectedAmount,
          fund,
          donor_email: userEmail || undefined,
          note: note.trim() || undefined,
          redirect: true,
        }, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });
        if (res.data.approval_url) {
          window.location.href = res.data.approval_url;
        } else {
          toast.error("Unable to start PayPal checkout. Please try again.");
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Failed to create donation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-navy py-16 text-white">
        <div className="container-page text-center">
          <div className="gold-line-center mb-4" />
          <h1 className="font-heading text-4xl sm:text-5xl mb-4">Online Giving</h1>
          <p className="text-white/60 text-lg">Sow into the ministry — plant into the kingdom</p>
        </div>
      </section>

      <div className="container-page py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left - Image + Message */}
          <div>
            <img src={GIVING_IMG} alt="People in prayer" className="w-full h-72 object-cover rounded-sm shadow-md mb-8" />
            <div className="gold-line mb-4" />
            <h2 className="font-heading text-2xl text-navy mb-4">Sowing Into the Kingdom</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              "Give, and it will be given to you. A good measure, pressed down, shaken together and running over, will be poured into your lap." &mdash; Luke 6:38
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Your generosity sows seeds into Word Alive Global Center and plants a harvest for the <strong className="text-navy">Jerry O'Neal Scholarship Fund</strong> — helping students pursue their God-given calling.
            </p>
            <p className="text-gray-500 leading-relaxed text-sm italic">
              Every seed you plant into this ministry goes toward apostolic teaching, community outreach, and equipping the next generation of Kingdom leaders.
            </p>
          </div>

          {/* Right - Donation Form */}
          <div className="space-y-4">
            {!isSignedIn && !loginBannerDismissed && (
              <div
                data-testid="login-suggestion-banner"
                className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-sm px-4 py-3"
              >
                <div className="flex items-center gap-2 text-blue-800 text-sm">
                  <LogIn className="h-4 w-4 flex-shrink-0" />
                  <span>Sign in to track your giving history and manage recurring gifts.</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    data-testid="login-suggestion-sign-in"
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 h-7"
                    onClick={() => {
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/auth-callback")}`;
                    }}
                  >
                    Sign In
                  </Button>
                  <button
                    data-testid="login-suggestion-dismiss"
                    onClick={() => setLoginBannerDismissed(true)}
                    className="text-blue-400 hover:text-blue-600 text-lg leading-none px-1"
                    aria-label="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}
          <Card className="bg-white shadow-md border-t-4 border-gold" data-testid="donation-form">
            <CardContent className="p-8 space-y-6">
              {/* Fund Selection */}
              <div>
                <Label className="text-navy font-medium mb-3 block">Select Fund</Label>
                <RadioGroup value={fund} onValueChange={setFund} className="space-y-3">
                  <label className={`flex items-center gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${fund === "general" ? "border-gold bg-gold/5" : "border-bone-dark hover:border-gold/40"}`}>
                    <RadioGroupItem value="general" data-testid="fund-general" />
                    <Heart className="h-5 w-5 text-gold" />
                    <div>
                      <p className="font-medium text-navy">General Fund</p>
                      <p className="text-sm text-gray-500">Support the 5-fold ministry's daily work, outreach & apostolic teaching</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${fund === "scholarship" ? "border-gold bg-gold/5" : "border-bone-dark hover:border-gold/40"}`}>
                    <RadioGroupItem value="scholarship" data-testid="fund-scholarship" />
                    <GraduationCap className="h-5 w-5 text-gold" />
                    <div>
                      <p className="font-medium text-navy">Jerry O'Neal Scholarship Fund</p>
                      <p className="text-sm text-gray-500">Plant a seed helping a student answer their God-given calling</p>
                    </div>
                  </label>
                  {givingPages.map((gp) => {
                    const val = `page:${gp.slug}`;
                    const label = gp.hero_heading || gp.title || gp.slug;
                    return (
                      <label key={val} className={`flex items-center gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${fund === val ? "border-gold bg-gold/5" : "border-bone-dark hover:border-gold/40"}`}
                        data-testid={`fund-page-${gp.slug}`}>
                        <RadioGroupItem value={val} data-testid={`fund-radio-${gp.slug}`}
                          onClick={() => { if (!note) setNote(label); }} />
                        {gp.image_url
                          ? <img src={gp.image_url} alt="" className="h-10 w-10 rounded-sm object-cover flex-shrink-0 border border-bone-dark" />
                          : <Heart className="h-5 w-5 text-gold" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-navy leading-snug">{label}</p>
                          {gp.giving_description && <p className="text-sm text-gray-500">{gp.giving_description}</p>}
                          <Link to={`/p/${gp.slug}`} className="text-xs text-gold hover:underline inline-flex items-center gap-1 mt-0.5"
                            data-testid={`fund-page-link-${gp.slug}`}>
                            Learn more <ExternalLink className="h-3 w-3" />
                          </Link>
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              </div>

              {/* Amount Selection */}
              <div>
                <Label className="text-navy font-medium mb-3 block">Select Amount</Label>
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3">
                  {PRESET_AMOUNTS.map((a) => (
                    <Button
                      key={a}
                      data-testid={`amount-${a}`}
                      variant={amount === String(a) ? "default" : "outline"}
                      className={amount === String(a) ? "btn-navy" : "border-gold/40 text-navy rounded-sm hover:border-gold"}
                      onClick={() => { setAmount(String(a)); setCustomAmount(""); }}
                    >
                      ${a}
                    </Button>
                  ))}
                  <Button
                    data-testid="amount-custom"
                    variant={amount === "custom" ? "default" : "outline"}
                    className={amount === "custom" ? "btn-navy" : "border-gold/40 text-navy rounded-sm hover:border-gold"}
                    onClick={() => setAmount("custom")}
                  >
                    Custom
                  </Button>
                </div>
                {amount === "custom" && (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      data-testid="custom-amount-input"
                      type="number"
                      min="1"
                      step="0.01"
                      placeholder="Enter amount"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="pl-7 border-bone-dark focus:border-gold focus:ring-gold rounded-sm"
                    />
                  </div>
                )}
              </div>

              {/* Gift Memo / Seed Name */}
              <div>
                <Label htmlFor="giving-memo" className="text-navy font-medium mb-1.5 block">
                  Memo <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id="giving-memo"
                  data-testid="giving-memo-input"
                  type="text"
                  maxLength={100}
                  placeholder="e.g., For Albania trips"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="border-bone-dark focus:border-gold focus:ring-gold rounded-sm"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Name your seed — or note where you'd like your gift to go. This note travels with your gift to PayPal.
                </p>
              </div>

              {/* Recurring Gift Toggle */}
              <div className="border-t border-bone-dark pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-gold" />
                    <Label htmlFor="recurring-toggle" className="text-sm text-navy font-medium cursor-pointer">
                      Make this a recurring gift
                    </Label>
                  </div>
                  <Switch
                    id="recurring-toggle"
                    data-testid="recurring-toggle"
                    checked={isRecurring}
                    onCheckedChange={(v) => { if (!isSignedIn) return; setIsRecurring(v); }}
                    disabled={!isSignedIn}
                  />
                </div>
                {!isSignedIn && (
                  <div className="flex items-center gap-2 text-xs text-gray-400 pl-6" data-testid="recurring-auth-hint">
                    <LogIn className="h-3.5 w-3.5" />
                    <span>Sign in to set up recurring giving</span>
                  </div>
                )}

                {isRecurring && isSignedIn && (
                  <div className="space-y-3 pl-6 animate-in fade-in-0 slide-in-from-top-1 duration-200">
                    {/* Frequency Select */}
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">Frequency</Label>
                      <Select value={frequency} onValueChange={setFrequency}>
                        <SelectTrigger className="border-bone-dark focus:border-gold focus:ring-gold rounded-sm" data-testid="frequency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Bi-weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Start Date Picker */}
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">Start Date</Label>
                      <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full justify-start text-left font-normal border-bone-dark focus:border-gold rounded-sm h-9"
                            data-testid="start-date-picker"
                          >
                            <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                            {startDate
                              ? new Date(startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                              : "Today (default)"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0"
                          align="start"
                          onPointerDownOutside={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        >
                          <Calendar
                            mode="single"
                            selected={startDate ? new Date(startDate) : undefined}
                            onSelect={(d) => {
                              setStartDate(d ? d.toISOString() : null);
                              setStartDateOpen(false);
                            }}
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                            fromDate={new Date()}
                            className="rounded-md border"
                          />
                          {startDate && (
                            <div className="px-3 pb-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="w-full text-xs text-gray-500"
                                onClick={() => { setStartDate(null); setStartDateOpen(false); }}
                              >
                                Clear (start today)
                              </Button>
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                      {startDate && (
                        <p className="text-xs text-gold mt-1">
                          First charge: {new Date(startDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                        </p>
                      )}
                    </div>
                    {/* Cover Fees */}
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="cover-fees-label">
                      <Checkbox
                        data-testid="cover-fees-checkbox"
                        checked={coverFees}
                        onCheckedChange={setCoverFees}
                        className="data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                      />
                      <span className="text-sm text-gray-600">
                        Cover processing fees
                        {selectedAmount > 0 && coverFees && (
                          <span className="text-xs text-gray-400 ml-1">
                            (total: ${((selectedAmount + 0.30) / (1 - 0.029)).toFixed(2)})
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Remember Preference */}
              <div className="flex items-center justify-between py-3 border-t border-bone-dark">
                <Label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer">Remember my giving preference</Label>
                <Switch id="remember" data-testid="remember-toggle" checked={remember} onCheckedChange={setRemember} />
              </div>

              {/* Submit */}
              <Button
                data-testid="donate-btn"
                className="w-full btn-gold py-3 uppercase tracking-wider font-medium text-base"
                onClick={handleDonate}
                disabled={loading || !selectedAmount || selectedAmount < 1}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : (isRecurring ? <Repeat className="h-4 w-4 mr-2" /> : <Heart className="h-4 w-4 mr-2" />)}
                {loading ? "Processing..." : isRecurring
                  ? `Give ${selectedAmount ? `$${selectedAmount.toFixed(2)}` : ""} ${frequency === "biweekly" ? "Bi-weekly" : frequency.charAt(0).toUpperCase() + frequency.slice(1)}`
                  : `Donate ${selectedAmount ? `$${selectedAmount.toFixed(2)}` : ""}`
                }
              </Button>

              <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                <Shield className="h-3.5 w-3.5" />
                <span>Secure payment via PayPal. Word Alive Global Center is a 501(c)(3) organization.</span>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
